import { useState, useEffect, useRef } from 'react';

interface FloatingCtaProps {
  href: string;
  label?: string;
  scrollThreshold?: number;
  hideTargetId?: string;
}

export default function FloatingCta({
  href,
  label = '無料診断を予約する',
  scrollThreshold = 300,
  hideTargetId = 'floating-cta-hide',
}: FloatingCtaProps) {
  const [scrolled, setScrolled] = useState(false);
  const [hiddenBySection, setHiddenBySection] = useState(false);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > scrollThreshold);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollThreshold]);

  useEffect(() => {
    const attachObserver = (target: Element) => {
      intersectionObserverRef.current?.disconnect();
      const observer = new IntersectionObserver(
        ([entry]) => {
          setHiddenBySection(entry.isIntersecting);
        },
        { threshold: 0.05 }
      );
      observer.observe(target);
      intersectionObserverRef.current = observer;
    };

    // すでにDOMに存在する場合はすぐ設定
    const existing = document.getElementById(hideTargetId);
    if (existing) {
      attachObserver(existing);
      return () => intersectionObserverRef.current?.disconnect();
    }

    // まだDOMにない場合はMutationObserverで出現を待つ
    const mutationObserver = new MutationObserver(() => {
      const target = document.getElementById(hideTargetId);
      if (target) {
        mutationObserver.disconnect();
        attachObserver(target);
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      intersectionObserverRef.current?.disconnect();
    };
  }, [hideTargetId]);

  const visible = scrolled && !hiddenBySection;

  return (
    <div
      className={`fixed bottom-6 right-4 z-50 hidden md:block transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'
      }`}
    >
      <div className="relative">
        {visible && (
          <span
            className="absolute inset-0 rounded-full animate-ping pointer-events-none"
            style={{ backgroundColor: '#3E5F8F', opacity: 0.18 }}
          />
        )}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="relative flex items-center gap-2 px-6 py-3.5 rounded-full text-white text-sm font-bold shadow-xl hover:opacity-90 active:scale-95 transition-all duration-150 whitespace-nowrap cursor-pointer"
          style={{ backgroundColor: '#3E5F8F' }}
        >
          <div className="flex items-center justify-center w-5 h-5">
            <i className="ri-calendar-check-line text-base" />
          </div>
          <span>{label}</span>
          <div className="flex items-center justify-center w-4 h-4">
            <i className="ri-arrow-right-line text-sm" />
          </div>
        </a>
      </div>
    </div>
  );
}
